import { createApp } from "vue";
import { captureError, installBrowserErrorReporting, recoverFromChunkError } from './services/errorReporting.js';
import App from "./App.vue";
import router from "./router";
import { authManager } from "./services/auth.js";
import toastService from "./services/toastService.js";
import { captureOriginalTrafficSource } from "./utils/cookies.js";
import { initializeHumblyticsAutoLabels } from "./services/analytics.js";
import "./assets/css/main.css";

// In dev, unregister any service workers so /api requests aren't intercepted (avoids
// "FetchEvent resulted in a network error" / "Failed to convert value to 'Response'" when
// an extension or stale SW handles the request).
if (typeof window !== "undefined" && "serviceWorker" in navigator) {
  const { hostname, port } = window.location;
  const isDev = hostname === "localhost" || hostname === "127.0.0.1" || port === "8080";
  if (isDev) {
    navigator.serviceWorker.getRegistrations().then((regs) => {
      regs.forEach((reg) => reg.unregister());
    });
  }
}

// Capture original referrer and UTM params BEFORE any OAuth redirects can overwrite them
// This must happen early, before any async operations
captureOriginalTrafficSource();

const EMBED_PATHS = new Set(["/cut30-embed", "/cut30embed-compact", "/cut30-chart-embed", "/cut30-chart-embed-2"]);
const normalizePathname = (pathname = "/") => {
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return pathname.slice(0, -1);
  }
  return pathname || "/";
};
const currentPathname = normalizePathname(
  typeof window !== "undefined" ? window.location.pathname : "/"
);
const isEmbedPath = EMBED_PATHS.has(currentPathname);
const isAdminPath = currentPathname === "/admin" || currentPathname.startsWith("/admin/");

const app = createApp(App);

let revealObserver = null;
const getRevealObserver = () => {
  if (revealObserver || typeof window === "undefined") return revealObserver;
  revealObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add("is-visible");
      revealObserver.unobserve(entry.target);
    });
  }, { rootMargin: "0px 0px -8% 0px", threshold: 0.12 });
  return revealObserver;
};

app.directive("reveal", {
  mounted(el, binding) {
    const reducedMotion = typeof window !== "undefined"
      && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    const config = typeof binding.value === "object" && binding.value !== null
      ? binding.value
      : { type: binding.value };
    const classes = Array.isArray(config.type)
      ? config.type
      : String(config.type || "vdb-reveal-up").split(" ");

    el.classList.add("vdb-reveal", ...classes.filter(Boolean));
    if (config.delay !== undefined) {
      el.style.setProperty("--reveal-delay", `${config.delay}ms`);
    }

    if (reducedMotion) {
      el.classList.add("is-visible");
      return;
    }

    const observer = getRevealObserver();
    if (observer) {
      observer.observe(el);
      el.__vdbRevealObserver = observer;
    } else {
      el.classList.add("is-visible");
    }
  },
  beforeUnmount(el) {
    el.__vdbRevealObserver?.unobserve(el);
    delete el.__vdbRevealObserver;
  },
});

// Global error handler
app.config.errorHandler = (err, vm, info) => {
  if (recoverFromChunkError(err)) return;
  captureError(err, { source: 'vue', component: vm?.$options?.name, code: info });
  console.error("Vue Error:", err);
  console.error("Component:", vm);
  console.error("Info:", info);
};

installBrowserErrorReporting();

// Global properties
app.config.globalProperties.$log = console.log;
app.config.globalProperties.$error = console.error;
app.config.globalProperties.$auth = authManager;
app.config.globalProperties.$toast = toastService;

// Create a simple event bus for Vue 3 compatibility
app.config.globalProperties.$eventBus = {
  events: {},
  $on(event, callback) {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(callback);
  },
  $off(event, callback) {
    if (!this.events[event]) return;
    if (callback) {
      this.events[event] = this.events[event].filter(cb => cb !== callback);
    } else {
      this.events[event] = [];
    }
  },
  $emit(event, ...args) {
    if (!this.events[event]) return;
    this.events[event].forEach(callback => callback(...args));
  }
};

app.use(router);

// Recover from chunk load failures (e.g. after a deploy changes chunk hashes).
// A full page reload fetches the updated manifest and retries the import.
router.onError((error, to) => {
  if (recoverFromChunkError(error, to.fullPath)) return;
  captureError(error, { source: 'browser', code: 'router_error' });
});

const mountApp = () => {
  clearTimeout(window.__VDB_SSR_FALLBACK_TIMER__);
  app.mount("#app");
  document.documentElement.classList.remove("vdb-js");
  initializeHumblyticsAutoLabels();

  // Keep analytics off the critical path for the initial route and its LCP.
  if (!isEmbedPath && !isAdminPath) {
    import("@vercel/analytics")
      .then(({ inject }) => inject())
      .catch((error) => console.warn("Vercel Analytics failed to load:", error));
  }
};

// Public pages arrive with useful server-rendered content in #app. Wait for the
// async route component before replacing it so the page never flashes an empty
// application shell with the footer inside the viewport.
router.isReady().then(mountApp, (error) => {
  console.error("Router failed to become ready:", error);
  clearTimeout(window.__VDB_SSR_FALLBACK_TIMER__);
  document.documentElement.classList.remove("vdb-js");
  // Keep valid middleware-rendered content intact if the initial route chunk
  // fails. Pages without a server fallback can still render the app shell.
  if (!document.querySelector(".vdb-ssr-content")) {
    mountApp();
  }
});
